import React, { useState } from 'react';
import ChartTooltip from '../components/ChartTooltip';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getCustomReport, parseReport } from '../api';
import ChartCard from '../components/ChartCard';

const ALL_METRICS = [
  'activeUsers','sessions','screenPageViews','bounceRate','averageSessionDuration',
  'newUsers','eventCount','engagementRate','totalUsers','userEngagementDuration',
  'sessionConversionRate','crashAffectedUsers','crashFreeUsersRate',
];

const ALL_DIMS = [
  'date','country','city','deviceCategory','browser','operatingSystem',
  'sessionDefaultChannelGroup','pagePath','pageTitle','eventName',
  'newVsReturning','platform','language','appVersion',
];

const COLORS = ['#38bdf8','#818cf8','#34d399','#f472b6','#fb923c','#fbbf24'];

export default function CustomReport({ propertyId, startDate, endDate }) {
  const [selMetrics, setSelMetrics] = useState(['activeUsers', 'sessions']);
  const [selDims, setSelDims] = useState(['date']);
  const [chartType, setChartType] = useState('bar');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);

  const toggle = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  };

  const run = async () => {
    if (!selMetrics.length || !selDims.length || !propertyId) return;
    setLoading(true);
    try {
      const res = await getCustomReport(propertyId, startDate, endDate, selMetrics, selDims);
      const rows = parseReport(res.data).slice(0, 30);
      setData(rows);
      setRan(true);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const dimKey = selDims[0] || 'date';
  const shortKey = (v) => v?.length > 20 ? v.slice(0, 20) + '…' : v;

  return (
    <div>
      <div style={styles.builder}>
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Select Metrics</div>
          <div style={styles.pills}>
            {ALL_METRICS.map(m => (
              <button key={m} style={{ ...styles.pill, ...(selMetrics.includes(m) ? styles.pillActive : {}) }} onClick={() => toggle(ALL_METRICS, setSelMetrics, m)}>
                {m}
              </button>
            ))}
          </div>
        </div>
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Select Dimensions (first used as X-axis)</div>
          <div style={styles.pills}>
            {ALL_DIMS.map(d => (
              <button key={d} style={{ ...styles.pill, ...(selDims.includes(d) ? styles.pillActiveGreen : {}) }} onClick={() => toggle(ALL_DIMS, setSelDims, d)}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div style={styles.controls}>
          <div style={styles.chartToggle}>
            {['bar', 'line'].map(t => (
              <button key={t} style={{ ...styles.typeBtn, ...(chartType === t ? styles.typeBtnActive : {}) }} onClick={() => setChartType(t)}>
                {t === 'bar' ? '▮' : '∿'} {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <button style={styles.runBtn} onClick={run} disabled={loading}>
            {loading ? '⟳ Running...' : '▶ Run Report'}
          </button>
        </div>
      </div>

      {ran && data.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <ChartCard title={`Custom Report: ${selMetrics.join(', ')} by ${selDims.join(', ')}`} subtitle={`${data.length} rows`} span={1}>
            <ResponsiveContainer width="100%" height={340}>
              {chartType === 'bar' ? (
                <BarChart data={data.map(r => ({ ...r, [dimKey]: shortKey(r[dimKey]) }))} margin={{ top: 5, right: 10, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey={dimKey} tick={{ fill: '#475569', fontSize: 9, angle: -30, textAnchor: 'end' }} />
                  <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  {selMetrics.map((m, i) => <Bar key={m} dataKey={m} name={m} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />)}
                </BarChart>
              ) : (
                <LineChart data={data.map(r => ({ ...r, [dimKey]: shortKey(r[dimKey]) }))} margin={{ top: 5, right: 10, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey={dimKey} tick={{ fill: '#475569', fontSize: 9, angle: -30, textAnchor: 'end' }} />
                  <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  {selMetrics.map((m, i) => <Line key={m} type="monotone" dataKey={m} name={m} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />)}
                </LineChart>
              )}
            </ResponsiveContainer>
          </ChartCard>

          <div style={{ marginTop: 16 }}>
            <ChartCard title="Raw Data" subtitle="All results">
              <div style={styles.table}>
                <div style={{ ...styles.thead, gridTemplateColumns: `repeat(${selDims.length + selMetrics.length}, 1fr)` }}>
                  {[...selDims, ...selMetrics].map(h => <span key={h}>{h}</span>)}
                </div>
                {data.map((r, i) => (
                  <div key={i} style={{ ...styles.trow, gridTemplateColumns: `repeat(${selDims.length + selMetrics.length}, 1fr)` }}>
                    {selDims.map(d => <span key={d} style={{ color: 'var(--accent)', fontSize: 11 }}>{r[d]}</span>)}
                    {selMetrics.map(m => <span key={m} style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{typeof r[m] === 'number' ? r[m].toLocaleString() : r[m]}</span>)}
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>
        </div>
      )}

      {ran && data.length === 0 && (
        <div style={{ marginTop: 40, textAlign: 'center', color: 'var(--text3)' }}>No data found for the selected combination.</div>
      )}
    </div>
  );
}

const styles = {
  builder: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 20 },
  section: {},
  sectionTitle: { fontSize: 11, color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 },
  pills: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  pill: { padding: '5px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 20, color: 'var(--text2)', fontSize: 11, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' },
  pillActive: { background: 'rgba(56,189,248,0.15)', borderColor: 'var(--accent)', color: 'var(--accent)' },
  pillActiveGreen: { background: 'rgba(52,211,153,0.15)', borderColor: 'var(--accent3)', color: 'var(--accent3)' },
  controls: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  chartToggle: { display: 'flex', gap: 6 },
  typeBtn: { padding: '8px 14px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text2)', fontSize: 12, cursor: 'pointer' },
  typeBtnActive: { background: 'rgba(56,189,248,0.15)', borderColor: 'var(--accent)', color: 'var(--accent)' },
  runBtn: { padding: '10px 24px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#0a1628', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  table: { display: 'flex', flexDirection: 'column' },
  thead: { display: 'grid', padding: '8px 12px', fontSize: 10, color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', background: 'var(--surface2)', borderRadius: 8, gap: 8 },
  trow: { display: 'grid', padding: '10px 12px', fontSize: 12, color: 'var(--text2)', borderBottom: '1px solid var(--border2)', gap: 8 },
};
